import { createElement, Fragment } from "react"
import { chain, compact } from "lodash"
import { OpenInNew, PictureInPicture } from "@mui/icons-material"

export const GROUP_LABELS = {
  RISK_MODEL: "Risk Model",
  FACTOR_SENSITIVITY: "Factor Sensitivity",
  LICENSED_CONTENT_SETS: "Licensed",
  PROPRIETARY_CONTENT_SETS: "Proprietary",
  CUSTOM: "Custom",
}

export const GROUP_IDS = {
  RISK_MODEL: "riskModel",
  FACTOR_SENSITIVITY: "factorSensitivity",
  LICENSED_CONTENT_SETS: "licensedContentSets",
  PROPRIETARY_CONTENT_SETS: "proprietaryContentSets",
  CUSTOM: "custom",
}

const LAUNCH_VIEW_SECONDARY_ACTION_TOOLTIP = createElement(Fragment, null,
  createElement("span", { style: { display: "block" } }, "Open as new tab"),
)

const LAUNCH_VIEW_TERTIARY_ACTION_TOOLTIP = createElement(Fragment, null,
  createElement("span", { style: { display: "block" } }, "Open in floating window"),
)

function getRiskModelItem(id, label) {
  return {
    id,
    label,
    groupId: GROUP_IDS.RISK_MODEL,
    groupLabel: GROUP_LABELS.RISK_MODEL,
  }
}

function getFactorSensitivityItem(id, label) {
  return {
    id,
    label,
    groupId: GROUP_IDS.FACTOR_SENSITIVITY,
    groupLabel: GROUP_LABELS.FACTOR_SENSITIVITY,
  }
}

export function buildDefaultViewItems(isOpPro) {
  return compact([
    getRiskModelItem("summary", "Summary"),
    getRiskModelItem("exposure", "Exposure"),
    getRiskModelItem("performance", "Performance"),
    !isOpPro && getRiskModelItem("risk", "Risk"),
    getRiskModelItem("beta", "Beta"),
    getRiskModelItem("marginalContribution", "Marginal Contribution"),
    getFactorSensitivityItem("predictedFactorSensitivity", "Predicted Factor Sensitivity"),
    getFactorSensitivityItem("historicalFactorSensitivity", "Historical Factor Sensitivity"),
  ])
}

export function buildContentSetViewItems(licensedContentSets, proprietaryContentSets) {
  const licensed = chain(licensedContentSets)
    .sortBy("name")
    .map((contentSet) => ({
      id: contentSet.id,
      label: contentSet.name,
      groupId: GROUP_IDS.LICENSED_CONTENT_SETS,
      groupLabel: GROUP_LABELS.LICENSED_CONTENT_SETS,
    }))
    .value()
  const proprietary = chain(proprietaryContentSets)
    .sortBy("name")
    .map((contentSet) => ({
      id: contentSet.id,
      label: contentSet.name,
      groupId: GROUP_IDS.PROPRIETARY_CONTENT_SETS,
      groupLabel: GROUP_LABELS.PROPRIETARY_CONTENT_SETS,
    }))
    .value()
  return [...licensed, ...proprietary]
}

export function getOpenInNewTabMergeOptions(onOpenInNewTab) {
  return {
    icon: OpenInNew,
    tertiaryIcon: PictureInPicture,
    onOpenInNewTab,
    secondaryTooltip: LAUNCH_VIEW_SECONDARY_ACTION_TOOLTIP,
    tertiaryTooltip: LAUNCH_VIEW_TERTIARY_ACTION_TOOLTIP,
  }
}

function mergeSingleItemWithOrderAndSecondaryAction(item, order, {
  SecondaryActionIcon,
  TertiaryActionIcon,
  onOpenInNewTab,
  menuItemTooltip,
  secondaryTooltip,
  tertiaryTooltip,
}) {
  const itemWithOrder = { ...item, order }
  const canOpenInNewTab = !item.disabled && !item.onSelect && SecondaryActionIcon && onOpenInNewTab
  if (!canOpenInNewTab) {
    return itemWithOrder
  }
  if (menuItemTooltip != null) {
    itemWithOrder.menuItemTooltip = menuItemTooltip
  }
  itemWithOrder.secondaryAction = {
    icon: SecondaryActionIcon,
    onClick: (event) => onOpenInNewTab(item, event),
    ...(secondaryTooltip != null && { tooltip: secondaryTooltip }),
  }
  if (TertiaryActionIcon) {
    itemWithOrder.tertiaryAction = {
      icon: TertiaryActionIcon,
      onClick: () => onOpenInNewTab(item, { shiftKey: true }),
      ...(tertiaryTooltip != null && { tooltip: tertiaryTooltip }),
    }
  }
  return itemWithOrder
}

export function mergeItemsWithOrderAndSecondaryAction(sections, {
  icon: SecondaryActionIcon,
  tertiaryIcon: TertiaryActionIcon = PictureInPicture,
  onOpenInNewTab,
  menuItemTooltip,
  secondaryTooltip,
  tertiaryTooltip,
}) {
  const mergeContext = {
    SecondaryActionIcon,
    TertiaryActionIcon,
    onOpenInNewTab,
    menuItemTooltip,
    secondaryTooltip,
    tertiaryTooltip,
  }
  return sections.flat()
    .map((item, index) => mergeSingleItemWithOrderAndSecondaryAction(item, index + 1, mergeContext))
}
